<!--

Ver 0.2.0 - 2026-03-04
   - Modified original trip list to show speed graphs

-->

<template>
      <v-container fluid>

          <input type="range" v-model="limit" min="50" max="140" step="5" >Max Speed: {{ limit }} km/h <input/>

            <!-- Load more item -->
          <v-list-item
            :ripple="false"
            @click="showDatePicker = !showDatePicker"
            class="infoButton2"
          >
            <v-list-item-action>
              <v-icon x-large :color="showDatePicker ? 'grey' : 'white'">
                {{ showDatePicker ? 'mdi-chevron-up' : 'mdi-chevron-down-circle-outline' }}
              </v-icon>
            </v-list-item-action>

            <v-list-item-content>
              <v-list-item-title class=" text-center">
                <v-icon left color="white">mdi-calendar-month</v-icon>
                  <strong v-if="selectedDate" >
                      {{ formatDatePicker(selectedDate) }}
                  </strong>
                  <span v-else class="text--disabled">Date Not Set!</span>
              </v-list-item-title>

            </v-list-item-content>

            <v-list-item-action>
              <v-icon  x-large :color="showDatePicker ? 'grey' : 'white'">
                {{ showDatePicker ? 'mdi-chevron-up' : 'mdi-chevron-down-circle-outline' }}
              </v-icon>
            </v-list-item-action>

          </v-list-item>

          <v-expand-transition>
            <div v-if="showDatePicker">
              <!--
                 Changed:
                  - full-width
              -->
              <v-date-picker
              full-width
                v-model="selectedDate"
                color="primary"
                header-color="primary"
                no-title
                :max="maxSelectableDate"
                @input="onDateSelected"
              ></v-date-picker>

            </div>
          </v-expand-transition>

        <v-divider/>

        <v-list two-line>
          <template v-for="(dayItem, dayIndex) in dayList">
            <!-- Day Header -->
            <v-list-item :key="'day-' + dayIndex" ripple>
              <!-- Chevron icon to toggle expansion ONLY -->
              <v-list-item-action @click.stop="toggleDay(dayItem)">
                <v-icon>
                  {{ dayItem.expanded ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
                </v-icon>
              </v-list-item-action>

              <v-list-item-content>
                <v-list-item-title class="text-center">{{ dayItem.formatted }}</v-list-item-title>
                <v-list-item-subtitle>
                  <template v-if="dayItem.loading">
                    Loading trips...
                  </template>
                  <template v-else-if="dayItem.error">
                    0 trips • 0 violations
                  </template>
                  <template v-else>
                    {{ dayItem.tripCount }} trip{{ dayItem.tripCount !== 1 ? 's' : '' }}
                    • {{ dayItem.totalViolations }} violation{{ dayItem.totalViolations !== 1 ? 's' : '' }}
                  </template>
                </v-list-item-subtitle>

                <VehicleAvailabilityBar
                  :trips="dayItem.trips"
                  :day="new Date(dayItem.day)"
                  :show-ticks="true"
                  :operating-hours="{ start: 8, end: 17 }"
                  :shade-weekends="true"   />

              </v-list-item-content>

            </v-list-item>

            <!-- Expanded Trip Sublist (only shown when expanded) -->
            <template v-if="dayItem.expanded">
              <v-list-item
                v-for="(trip, tripIndex) in dayItem.trips"
                :key="trip.Id"
                class="pl-10"
                two-line
              >
                  <v-list-item-avatar size="48">
                          <RingProgAvatar
                        :start-time=formatTimev2(trip.StartTime)
                        :end-time=formatTimev2(trip.EndTime)
                          :size="48"
                          color="#1976D2"
                        />

                  </v-list-item-avatar>

                  <v-list-item-content>
                      <v-list-item-title>Beg: {{ formatTimev2(trip.StartTime) }} - End: {{ formatTimev2(trip.EndTime) }}</v-list-item-title>
                      <v-list-item-subtitle>

                        {{ metersToKm(trip.Distance) }} km - {{ secondsToHHmmss(trip.DurationSeconds) }} Hrs
                      </v-list-item-subtitle>

                      <SpeedSparkline    :trip-data="trip.PlotData" :threshold="limit"  />
                  </v-list-item-content>
                  <v-icon
                        x-large
                        color="primary"
                        @click.stop="goToTrip(trip,tripIndex)"
                      >
                        mdi-play-circle
                  </v-icon>

            </v-list-item>

            </template>

            <v-divider
              :key="'div-' + dayIndex"
              v-if="dayIndex < dayList.length - 1 || dayItem.expanded"
            />

       </template>

      <v-divider inset></v-divider>

      <v-subheader inset></v-subheader>

          <!-- Load more item -->
          <v-list-item @click="loadMore" ripple :disabled="loadingMore" class="infoButton2">
                <v-progress-circular v-if="loadingMore" indeterminate size="24" />
                <v-icon large v-else>mdi-chevron-down-circle-outline</v-icon>

              <v-list-item-content>
                <v-list-item-title class="text-center">( Load {{totalDays}} previous {{totalDays > 1? 'days': 'day'}} )</v-list-item-title>
              </v-list-item-content>
              <v-list-item-action>
                <v-progress-circular v-if="loadingMore" indeterminate size="24" />
                <v-icon large v-else>mdi-chevron-down-circle-outline</v-icon>
              </v-list-item-action>

          </v-list-item>

      </v-list>

        <v-card light="light" class="pa-2" v-if="debug">
                <p><strong>UnitID:</strong> {{ UnitID }}</p>
                <p><strong>selectedDate:</strong> {{ selectedDate }}</p>
        </v-card>

      </v-container>
</template>

<script>
import RingProgAvatar from '@/comps/trip-list/RingProgAvatar.vue'
import VehicleAvailabilityBar from '@/comps/trip-list/VehicleAvailabilityBar.vue'
import SpeedSparkline from '@/comps/trip-list/SpeedEventSparkline.vue'

import { format } from 'date-fns'

export default {
  components: {
    SpeedSparkline,
    VehicleAvailabilityBar,
    RingProgAvatar
  // ClockRingChart
  },
  props: {
    UnitID: {
      type: [String, Number],
      required: true
    },

    VehicleReg: {
      type: String,
      required: true
    },

    Driver: {
      type: String,
      default: null
    },

    VehicleID: {
      type: [String, Number],
      default: null
    },

    dayDate: {
      type: String,
      default: null
    },

    TripNum: {
      type: [Number],
      default: null
    }

  },
  data () {
    return {
      limit: 120,
      showDatePicker: true,

      //----------------------------
      totalDays: 4,
      debug: this.$debug,
      selectedDate: this.formatDay(new Date()),
      batchSize: 1,
      batchesLoaded: 1,
      dayList: [],
      loadingMore: false,

      test: false,
      test2: false
    }
  },
  created () {
    if (this.dayDate === null) {
      this.selectedDate = this.formatDay(new Date())
    } else {
      this.selectedDate = this.dayDate
    }

    if (this.test2) {
      this.UnitID = '350612074277334'
      this.selectedDate = '2026-02-24'
    }

    const initialDates = this.generateBatch(1)

    this.dayList = initialDates.map(d => this.createDayObject(d))
    this.dayList.forEach((day, index) => this.fetchDay2(day, index))
  },
  computed: {
    maxSelectableDate () {
      // Today is the latest allowed date
      return new Date().toISOString().split('T')[0]

      // Alternative: yesterday as max (if today should also be disallowed)
      // const yesterday = new Date()
      // yesterday.setDate(yesterday.getDate() - 1)
      // return yesterday.toISOString().split('T')[0]
    }
  },

  methods: {

    onDateSelected (value) {
      console.log('Selected:', value)
      // Optional: auto-close on selection
      this.showDatePicker = false

      this.selectedDate = value

      this.batchesLoaded = 1

      this.dayDate = value

      this.generateBatch(1)

      const initialDates = this.generateBatch(1)

      this.dayList = initialDates.map(d => this.createDayObject(d))

      this.dayList.forEach((day, index) => this.fetchDay2(day, index))
    },

    goToTrip (trip, tripIndex) {
      console.log('plotTrip')

      const route =
          '/trip-plot' +
          `/${trip.Id}` +
          `/${trip.UnitId}` +
          `/${this.VehicleReg}` +
          '/trip-list-speed' +
          `/${this.formatDay(trip.StartTime)}` +
          `/${tripIndex}`

      this.$router.push(route)
    },

    generateBatch (batchNum) {
      const dates = []
      const today = new Date(this.selectedDate)

      today.setHours(0, 0, 0, 0)

      const startOffset = batchNum === 1 ? 0 : this.batchSize + (batchNum - 2) * this.batchSize
      this.totalDays = batchNum === 1 ? this.batchSize : this.batchSize

      if (this.dayDate !== null) {
        this.totalDays = 1
      }

      for (let i = 0; i < this.totalDays; i++) {
        const d = new Date(today)
        d.setDate(today.getDate() - (startOffset + i))
        dates.push(this.formatDate(d))
      }
      return dates//.reverse(); // newest first
    },

    dayDateFormat (day) {
      return format(new Date(day), 'yyyy-MM-dd')
    },

    dayDateTime (day) {
      return format(new Date(day), 'HH:mm')
    },

    formatDatePicker (dateString) {
      if (!dateString) return ''
      const date = new Date(dateString)
      return date.toLocaleDateString('en-GB', {
        weekday: 'short',
        day: 'numeric',
        month: 'short',
        year: 'numeric'
      })
    },

    formatDate (dateObj) {
      const dateStr = dateObj
      const formatted = dateObj.toLocaleDateString('en-US', {
        weekday: 'long',
        month: 'long',
        day: 'numeric',
        year: 'numeric'
      })
      return { date: dateStr, formatted }
    },

    createDayObject (dateInfo) {
      return {
        day: this.formatDay(dateInfo.date),
        date: dateInfo.date,
        formatted: dateInfo.formatted,
        tripCount: 0,
        totalViolations: 0,
        trips: [],
        loading: false,
        loaded: false,
        error: false,
        expanded: false
      }
    },

    async fetchDay2 (dayObj, index) {
      if (dayObj.loaded || dayObj.loading) return

      dayObj.loading = true
      dayObj.error = false
      const startDate = `${dayObj.day}`

      let url = this.$baseUrl + `/api/v2/trip/plot-data?UnitID=${this.UnitID}&Day=${startDate}`

      const testUrl = '/api/trip-extracted.json'

      try {
        if (this.test && index === 0) {
          console.log(` ${index} - Loading test trip data from local JSON file...`)
          url = testUrl
        }

        const response = await this.$axiosCache.get(url)

        const trips = response.data || []

        // trips.sort((a, b) => new Date(b.StartTime) - new Date(a.StartTime))

        if (trips.length === 0) {
          if (this.$debug) {
            console.warn(`No trips found for ${dayObj.date}`)
          }

          dayObj.totalViolations = 0
          dayObj.tripCount = 0
          dayObj.loaded = true
          dayObj.trips = trips
          return
        }

        dayObj.day = this.formatDay(trips[0].StartTime)
        dayObj.trips = trips
        dayObj.tripCount = trips.length
        dayObj.totalViolations = trips.reduce((sum, t) => sum + (t.ViolationAllCount || 0), 0)
        dayObj.loaded = true

        if ((index === 0) && (this.dayDate !== null)) {
          dayObj.expanded = true
        }

        // this.toggleDay(dayObj)
      } catch (err) {
        if (this.$debug)
        { console.error(`Failed to load trips for ${dayObj.date}`, err) }

        dayObj.error = true
      } finally {
        dayObj.loading = false
      }
    },

    // Toggle only via chevron click
    async toggleDay (dayItem) {
      if (dayItem.loading) return

      if (!dayItem.loaded && !dayItem.error) {
        await this.fetchDay2(dayItem)
      }
      dayItem.expanded = !dayItem.expanded
    },

    async loadMore () {
      if (this.loadingMore) return
      this.loadingMore = true
      this.batchesLoaded += 1

      const newDates = this.generateBatch(this.batchesLoaded)
      const newDayObjects = newDates.map(d => this.createDayObject(d))

      this.dayList.push(...newDayObjects)

      for (const day of newDayObjects) {
        await this.fetchDay2(day)
      }

      this.loadingMore = false
    },

    metersToKm (meters) {
      return Math.round(meters / 100) / 10
    },

    secondsToHHmmss (seconds) {
      const hrs = Math.floor(seconds / 3600)
      const mins = Math.floor((seconds % 3600) / 60)
      const secs = Math.floor(seconds % 60)

      return (
        String(hrs).padStart(2, '0') + ':' +
            String(mins).padStart(2, '0') + ':' +
            String(secs).padStart(2, '0')
      )
    },

    secondsToHHmm (seconds) {
      const totalMinutes = Math.floor(seconds / 60)
      const hours = Math.floor(totalMinutes / 60)
      const minutes = totalMinutes % 60

      return String(hours).padStart(2, '0') + ':' +
                String(minutes).padStart(2, '0')
    },

    formatTime (time) {
      const time2 = format(new Date(time), 'HH:mm')

      console.log('Formatted time:', time2) // Debug log to verify the output

      return time2
    },

    formatDay (time) {
      return format(new Date(time), 'yyyy-MM-dd')
    },

    formatTimev2 (time) {
      const time2 = time.split('T')[1].slice(0, 5)

      if (this.$debug) {
        console.log('UnFormatted time:', time) // Debug log to verify the output
        console.log('  Formatted time:', time2) // Debug log to verify the output
      }

      return time2
    },

    formatDuration (seconds) {
      const h = Math.floor(seconds / 3600)
      const m = Math.floor((seconds % 3600) / 60)
      const s = seconds % 60
      return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`
    }
  }

}
</script>

<style scoped>

  .v-list-item__icon {

  }

   .v-list-item {
     padding-left: 4px !important;
     padding-right: 6px !important;
   }
   .v-list-item__content {
    padding-top: 4px !important;
   }

   .v-list-item__action {
     margin-left: 4px !important;
     margin-right: 6px !important;
   }

    .infoButton2 {
      background-color: #1976d2;
      /* display: inline-block; */
      text-decoration: none;
      color: #fff !important;
      /* color: inherit; */
      /* padding: 0.5rem 1rem; */
      border-radius: 4px;
      transition: all 0.3s;
    }

      .infoButton2:hover {
        background-color: #2196f3;
        /* background-color: #1976d2; */
        color: #fff;
      }

.pl-10 {
  padding-left: 8px !important;
  background-color: rgba(0, 0, 0, 0.02); /* Optional: subtle background for subitems */
}
</style>
